'use client';

import { c as _c } from "react/compiler-runtime";
import { DebateSkeleton } from '@/components/social/debate/debate-skeleton';
import { ThesisCard } from '@/components/social/debate/thesis-card';
import type { ThesisReplyWriteAccess } from '@/components/social/debate/thesis-reply-write-access';
import type { ThesisItem } from '@/components/social/types';
import { Separator } from '@/components/ui/app-separator';
import { EmptyState } from '@/components/ui/empty-state';
import { Comment03IconSolid } from '@/components/ui/icon/glyphs';
import { InfiniteList, type InfiniteListScrollMode } from '@/components/ui/infinite-list';
function dedupBySignature(items: ThesisItem[]): ThesisItem[] {
  const seen = new Set<string>();
  return items.filter(item => {
    if (seen.has(item.signature)) return false;
    seen.add(item.signature);
    return true;
  });
}

/**
 * The pagination + load-state contract, exactly as it arrives from the
 * `useGetThesesInfinite()` result at every call site. Grouped into one prop so
 * the feed receives the fetch state as the single cohesive unit it already is,
 * rather than four loose flags.
 */
interface ThesisFeedPagination {
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  /** Used only to gate the initial skeleton (`isLoading && pageCount === 0`). */
  pageCount: number;
}
interface ThesisFeedProps {
  theses: ThesisItem[];
  pagination: ThesisFeedPagination;
  isAuthenticated: boolean;
  userId: string | null;
  /** Shared market-level write access. Reading threads never depends on it. */
  replyWriteAccess?: ThesisReplyWriteAccess;
  /** Opens the owning market's mobile trade sheet. */
  onOpenTradeForm?: () => void;
  /** Empty-state headline — the primary "no theses" message. Pre-translated. */
  emptyTitle: string;
  /** Empty-state supporting copy below the headline. Pre-translated. */
  emptyDescription: string;
  /**
   * `'debate'` (default) is used inside the market-detail Debate tab; the
   * portfolio Theses panel passes `'portfolio'` so each row renders as a
   * pressable card with the parent market title.
   */
  variant?: 'debate' | 'portfolio';
  /** Cap on the scroll viewport. Default `560`. */
  maxHeight?: number | string;
  /** Let the page own scrolling below the desktop breakpoint. */
  scrollMode?: InfiniteListScrollMode;
}
const THESIS_FEED_DIVIDER_CLASS = 'h-[length:var(--border-width-hairline)] min-h-[0.5px] shrink-0 bg-border-secondary';
export function ThesisFeed(t0) {
  const $ = _c(26);
  const {
    theses,
    pagination,
    isAuthenticated,
    userId,
    replyWriteAccess,
    onOpenTradeForm,
    emptyTitle,
    emptyDescription,
    variant: t1,
    maxHeight: t2,
    scrollMode: t3
  } = t0;
  const variant = t1 === undefined ? "debate" : t1;
  const maxHeight = t2 === undefined ? 560 : t2;
  const scrollMode = t3 === undefined ? "contained" : t3;
  const {
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    pageCount
  } = pagination;
  let t4;
  if ($[0] !== theses) {
    t4 = dedupBySignature(theses);
    $[0] = theses;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const dedupedTheses = t4;
  let t5;
  if ($[2] !== isAuthenticated || $[3] !== onOpenTradeForm || $[4] !== replyWriteAccess || $[5] !== userId || $[6] !== variant) {
    t5 = thesis_0 => <ThesisCard thesis={thesis_0} isAuthenticated={isAuthenticated} userId={userId} {...replyWriteAccess !== undefined ? {
      replyWriteAccess
    } : {}} {...onOpenTradeForm !== undefined ? {
      onOpenTradeForm
    } : {}} variant={variant} />;
    $[2] = isAuthenticated;
    $[3] = onOpenTradeForm;
    $[4] = replyWriteAccess;
    $[5] = userId;
    $[6] = variant;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== variant) {
    t6 = variant === "debate" ? <Separator className={THESIS_FEED_DIVIDER_CLASS} /> : null;
    $[8] = variant;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isLoading && pageCount === 0;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DebateSkeleton />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== emptyDescription || $[12] !== emptyTitle) {
    t9 = <EmptyState icon={Comment03IconSolid} title={emptyTitle} description={emptyDescription} />;
    $[11] = emptyDescription;
    $[12] = emptyTitle;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = variant === "portfolio" ? "flex flex-col gap-y-4" : "flex flex-col gap-y-2";
  let t11;
  if ($[14] !== dedupedTheses || $[15] !== fetchNextPage || $[16] !== hasNextPage || $[17] !== isFetchingNextPage || $[18] !== maxHeight || $[19] !== scrollMode || $[20] !== t10 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t9) {
    t11 = <InfiniteList items={dedupedTheses} getItemKey={_temp} renderItem={t5} separator={t6} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} isInitialLoading={t7} loadingPlaceholder={t8} emptyState={t9} listClassName={t10} maxHeight={maxHeight} scrollMode={scrollMode} />;
    $[14] = dedupedTheses;
    $[15] = fetchNextPage;
    $[16] = hasNextPage;
    $[17] = isFetchingNextPage;
    $[18] = maxHeight;
    $[19] = scrollMode;
    $[20] = t10;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function _temp(thesis) {
  return thesis.signature;
}